import { Link } from "react-router-dom";
import { ArrowRight, Globe, Smartphone, Braces, CloudCog, ShieldCheck, Compass } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Reveal, Eyebrow } from "@/components/Reveal";

const serviceIcons = {
    web: Globe,
    mobile: Smartphone,
    api: Braces,
    cloud: CloudCog,
    security: ShieldCheck,
    consulting: Compass,
};

export default function Services() {
    const { t } = useLanguage();
    const s = t.servicesPage;

    return (
        <div data-testid="services-page" className="pt-36 sm:pt-44">
            <section className="relative mx-auto max-w-7xl px-5 sm:px-8">
                <div className="grid-bg absolute inset-0 -z-10" aria-hidden="true" />
                <Reveal>
                    <Eyebrow>{s.eyebrow}</Eyebrow>
                    <h1 className="max-w-3xl font-display text-4xl font-extrabold leading-[1.1] tracking-tight text-white sm:text-5xl lg:text-6xl">
                        {s.title}
                    </h1>
                    <p className="mt-6 max-w-2xl text-sm leading-relaxed text-slate-400 sm:text-base">{s.sub}</p>
                </Reveal>
            </section>

            <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
                <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                    {t.services.items.map((item, i) => {
                        const Icon = serviceIcons[item.id];
                        return (
                            <Reveal key={item.id} delay={0.06 * i} className={i % 3 === 1 ? "lg:translate-y-8" : ""}>
                                <div
                                    className="glass card-lift group flex h-full flex-col rounded-2xl p-7 sm:p-8"
                                    data-testid={`services-card-${item.id}`}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-slate-700 bg-ink text-neon transition-colors duration-300 group-hover:border-neon/50">
                                            <Icon className="h-5 w-5" />
                                        </div>
                                        <span className="font-mono text-xs text-slate-600">0{i + 1}</span>
                                    </div>
                                    <h2 className="mt-6 font-display text-lg font-bold text-white sm:text-xl">
                                        {item.title}
                                    </h2>
                                    <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-400">{item.desc}</p>
                                    <div className="mt-6 flex flex-wrap gap-2">
                                        {item.tags.map((tag) => (
                                            <span
                                                key={tag}
                                                className="rounded-full border border-slate-800 bg-ink px-2.5 py-1 font-mono text-[10px] text-slate-500"
                                            >
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </Reveal>
                        );
                    })}
                </div>
            </section>

            {/* PROCESS */}
            <section className="border-t border-slate-800/60 bg-ink2/30">
                <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
                    <Reveal>
                        <Eyebrow>{s.processEyebrow}</Eyebrow>
                        <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
                            {s.processTitle}
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid gap-5 lg:grid-cols-5">
                        {s.steps.map((step, i) => (
                            <Reveal key={i} delay={0.08 * i}>
                                <div
                                    className="glass card-lift relative h-full rounded-2xl p-6"
                                    data-testid={`process-step-${i}`}
                                >
                                    <span className="font-display text-3xl font-extrabold text-gradient">
                                        0{i + 1}
                                    </span>
                                    <h3 className="mt-4 font-display text-base font-bold text-white">{step.title}</h3>
                                    <p className="mt-3 text-xs leading-relaxed text-slate-400 sm:text-sm">
                                        {step.desc}
                                    </p>
                                    {i < s.steps.length - 1 && (
                                        <span className="absolute -right-3 top-1/2 hidden h-px w-6 bg-neon/30 lg:block" aria-hidden="true" />
                                    )}
                                </div>
                            </Reveal>
                        ))}
                    </div>

                    <Reveal className="mt-16 text-center">
                        <Link
                            to="/lien-he"
                            data-testid="services-cta-button"
                            className="group inline-flex items-center gap-2 rounded-full bg-neon px-8 py-4 text-sm font-semibold text-ink transition-colors hover:bg-white"
                        >
                            {t.nav.cta}
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
